import { useCallback, useEffect, useRef, useState } from "react";

/** Pause after the last change before a save starts. Long enough to stop a save on every keystroke. */
export const AUTOSAVE_DELAY_MS = 1500;

/**
 * Saves an editor's data while it is being edited.
 *
 * - The data the editor opened with counts as saved: nothing is written until something changes.
 * - Changes are saved once the user pauses for `delay` ms, however many changes came before the pause.
 * - Saves never overlap. A change made while a save is running is saved as soon as that one finishes.
 * - A failed save is reported and not retried by itself; the next change tries again.
 *
 * @param {{ enabled: boolean, data: unknown, save: (data: unknown) => Promise<unknown>, delay?: number }} options
 * @returns {{ status: "idle" | "saving" | "saved" | "error", savedAt: Date | null, error: string | null, flush: () => Promise<void>, settle: () => Promise<void> }}
 */
export default function useAutoSave({ enabled, data, save, delay = AUTOSAVE_DELAY_MS }) {
  const serialized = JSON.stringify(data);
  const [status, setStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const lastSaved = useRef(serialized);
  const latest = useRef({ data, serialized });
  latest.current = { data, serialized };
  const saveRef = useRef(save);
  saveRef.current = save;
  const timer = useRef(null);
  const inFlight = useRef(null);

  const run = async () => {
    for (;;) {
      const { data: payload, serialized: snapshot } = latest.current;
      if (snapshot === lastSaved.current) {
        return;
      }
      setStatus("saving");
      setError(null);
      try {
        await saveRef.current(payload);
        lastSaved.current = snapshot;
        setSavedAt(new Date());
        setStatus("saved");
      } catch (e) {
        setStatus("error");
        setError(e?.response?.data?.message || e?.message || "Could not save. Your changes are still here.");
        return;
      }
    }
  };

  const runSave = useCallback(() => {
    if (!inFlight.current) {
      inFlight.current = run().finally(() => { inFlight.current = null; });
    }
    return inFlight.current;
    // run reads only refs, so it never changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enabled || serialized === lastSaved.current) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { runSave(); }, delay);
    return () => clearTimeout(timer.current);
  }, [enabled, serialized, delay, runSave]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (!enabled) return;
    await runSave();
  }, [enabled, runSave]);

  // For an explicit Save: drop the waiting timer and let a save that is already running finish, so the explicit
  // save can never race it into creating a second copy.
  const settle = useCallback(async () => {
    clearTimeout(timer.current);
    if (inFlight.current) await inFlight.current;
  }, []);

  return { status, savedAt, error, flush, settle };
}
